import { Link } from 'react-router-dom';
import { Store, BookOpen, Facebook, Instagram, Twitter, Sun, Moon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useTheme } from '@/hooks/useTheme';
import type { FooterProps } from '../Footer';
import { useFooterData } from './useFooterData';
import FooterLinkItem from './FooterLinkItem';

const LINK_CLASS =
  'text-ink-soft hover:text-ink transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 rounded';
const SOCIAL_CLASS =
  'text-ink-muted hover:text-ink transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600';

/**
 * The `app_footer` variant (Nightfall and other app-launch templates). On
 * the page surface — it follows the colour scheme instead of being a fixed
 * dark band — with the brand and socials on the left, three link columns
 * (the tenant's shop/explore links, support links, legal), the theme toggle,
 * and the copyright row with the legal-entity line and support phone the
 * other variants carry. A template's store badges slot in beside the brand
 * in the next phase.
 */
export default function AppFooter({ variant = 'app_footer' }: FooterProps = {}) {
  const { t } = useTranslation(['footer', 'nav', 'common']);
  const { theme, toggleTheme } = useTheme();
  const d = useFooterData();
  const BrandIcon = d.showCourses && !d.showEcommerce ? BookOpen : Store;

  return (
    <footer role="contentinfo" data-footer-variant={variant} className="border-t border-line bg-surface-soft text-ink">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-5">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2">
              <BrandIcon className="h-6 w-6 text-sage-600" aria-hidden="true" />
              <span className="font-display text-lg font-bold text-ink">{d.storeName}</span>
            </div>
            <p className="mt-3 max-w-sm text-sm leading-relaxed text-ink-soft">
              {d.footerAboutDisplay}
              {d.footerAboutNeedsTruncation && (
                <>
                  {' '}
                  <Link to="/page/about" className="underline underline-offset-2 text-ink hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 rounded">
                    {t('footer:readMore', { defaultValue: 'Read more' })}
                  </Link>
                </>
              )}
            </p>
            {d.hasSocials && (
              <div className="mt-4 -ml-2 flex gap-1">
                {d.facebookUrl && (
                  <a href={d.facebookUrl} target="_blank" rel="noopener noreferrer" className={SOCIAL_CLASS} aria-label={t('footer:social.facebook')}>
                    <Facebook className="h-5 w-5" />
                  </a>
                )}
                {d.instagramUrl && (
                  <a href={d.instagramUrl} target="_blank" rel="noopener noreferrer" className={SOCIAL_CLASS} aria-label={t('footer:social.instagram')}>
                    <Instagram className="h-5 w-5" />
                  </a>
                )}
                {d.twitterUrl && (
                  <a href={d.twitterUrl} target="_blank" rel="noopener noreferrer" className={SOCIAL_CLASS} aria-label={t('footer:social.twitter')}>
                    <Twitter className="h-5 w-5" />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Shop / Explore */}
          <div>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink">{d.shopSectionTitle}</h2>
            <ul className="space-y-2 text-sm">
              {d.shopLinks.map((link, i) => (
                <li key={i}><FooterLinkItem link={link} className={LINK_CLASS} /></li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink">{t('footer:support.heading')}</h2>
            <ul className="space-y-2 text-sm">
              {d.supportLinks.map((link, i) => (
                <li key={i}><FooterLinkItem link={link} className={LINK_CLASS} /></li>
              ))}
            </ul>
          </div>

          {/* Legal + theme */}
          <div>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink">{t('footer:legal.heading')}</h2>
            <ul className="space-y-2 text-sm">
              <li><Link to="/page/privacy-policy" className={LINK_CLASS}>{t('footer:legal.privacy')}</Link></li>
              <li><Link to="/page/terms-of-service" className={LINK_CLASS}>{t('footer:legal.terms')}</Link></li>
              {/* §512(c)(2) wants the designated agent reachable from the public
                  site, not just on file with the Copyright Office. Every footer
                  variant carries this link — don't drop it in a tidy-up. */}
              <li><Link to="/copyright" className={LINK_CLASS}>{t('footer:legal.copyright', { defaultValue: 'Copyright' })}</Link></li>
            </ul>
            <button
              type="button"
              onClick={toggleTheme}
              className={cn(
                'mt-5 inline-flex items-center gap-2 rounded-full border border-line px-3 py-1.5 text-xs font-medium text-ink-soft hover:text-ink hover:bg-surface-muted transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600',
              )}
              aria-label={theme === 'light' ? t('common:accessibility.darkTheme') : t('common:accessibility.lightTheme')}
            >
              {theme === 'light' ? <Moon className="h-4 w-4" aria-hidden="true" /> : <Sun className="h-4 w-4" aria-hidden="true" />}
              {theme === 'light' ? t('common:accessibility.darkTheme') : t('common:accessibility.lightTheme')}
            </button>
          </div>
        </div>

        <div className="mt-10 flex flex-col items-start justify-between gap-3 border-t border-line pt-6 text-sm text-ink-muted sm:flex-row sm:items-center">
          <p>
            {t('footer:copyright', { year: new Date().getFullYear(), name: d.storeName })}
            {d.legalEntityLine}
            {d.storePhone && (
              <>
                {' '}
                <a href={`tel:${d.storePhoneHref}`} className="hover:text-ink transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-600 rounded">
                  {d.storePhone}
                </a>
              </>
            )}
          </p>
        </div>
      </div>
    </footer>
  );
}
